import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import LightbulbOutlinedIcon from "@mui/icons-material/LightbulbOutlined";
import { Box, CircularProgress, Typography } from "@mui/material";
import { styled } from "@mui/material/styles";
import {
  useNutrientStrengths,
  useNutrientWeaknesses,
  useRecommendations,
} from "../hooks/useInsightsDashboardQueries";

export function WaterSoilTab({ farmerGuid }: { farmerGuid?: string }) {
  const { data: strengthsData, isLoading: strengthsLoading } =
    useNutrientStrengths(farmerGuid);
  const { data: weaknessesData, isLoading: weaknessesLoading } =
    useNutrientWeaknesses(farmerGuid);
  const { data: recommendationsData, isLoading: recommendationsLoading } =
    useRecommendations(farmerGuid);

  const strengths = (strengthsData?.data ?? []).map(
    (r) => r["NutrientManagement.description"]
  );
  const weaknesses = (weaknessesData?.data ?? []).map(
    (r) => r["NutrientManagement.description"]
  );
  const recommendations = (recommendationsData?.data ?? [])
    .map((r) =>
      r["ActionsLegacy.action"]
        .replace(
          /[\u2610\u2611\u2612\u25A1\u25A0\u25FB\u25FC\uFFFD☐☑☒□■◻◼\u{1F532}\u{1F533}\u{2B1B}\u{2B1C}]/gu,
          ""
        )
        .trim()
    )
    .filter(Boolean);

  const isLoading = strengthsLoading || weaknessesLoading;
  const hasData = strengths.length > 0 || weaknesses.length > 0;

  return (
    <Box sx={{ maxWidth: 720, mx: "auto" }}>
      {/* Overview */}
      <Card>
        <Typography
          variant="subtitle2"
          sx={{ fontWeight: 600, color: "#18181b", mb: 1 }}
        >
          Water & Soil Quality
        </Typography>
        <Typography variant="body2" sx={{ color: "#52525b", lineHeight: 1.7 }}>
          A summary of your farm's nutrient management performance based on your
          soil and water assessments. Strengths highlight what you're doing
          well; areas for improvement show where changes could benefit water
          quality, soil health, and nutrient efficiency.
        </Typography>
      </Card>

      {isLoading ? (
        <Card>
          <Box sx={{ textAlign: "center", py: 4 }}>
            <CircularProgress size={32} />
          </Box>
        </Card>
      ) : !hasData ? (
        <Card>
          <Typography variant="body2" sx={{ color: "#71717a" }}>
            No nutrient management data available yet. Results will appear here
            once your soil and water assessments have been processed.
          </Typography>
        </Card>
      ) : (
        <>
          {/* Score summary */}
          <Card>
            <Box
              sx={{
                display: "flex",
                gap: 3,
                mb: 2,
              }}
            >
              <Box sx={{ flex: 1, textAlign: "center" }}>
                <Typography
                  sx={{
                    fontSize: "2rem",
                    fontWeight: 700,
                    color: "#2E7831",
                    lineHeight: 1.2,
                  }}
                >
                  {strengths.length}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{ color: "#71717a", fontWeight: 500 }}
                >
                  Strengths
                </Typography>
              </Box>
              <Box sx={{ flex: 1, textAlign: "center" }}>
                <Typography
                  sx={{
                    fontSize: "2rem",
                    fontWeight: 700,
                    color: weaknesses.length > 0 ? "#A48137" : "#2E7831",
                    lineHeight: 1.2,
                  }}
                >
                  {weaknesses.length}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{ color: "#71717a", fontWeight: 500 }}
                >
                  Areas to Improve
                </Typography>
              </Box>
            </Box>

            {/* Ratio bar */}
            {(strengths.length > 0 || weaknesses.length > 0) && (
              <Box
                sx={{
                  display: "flex",
                  height: 8,
                  borderRadius: 1,
                  overflow: "hidden",
                  gap: "2px",
                }}
              >
                <Box
                  sx={{
                    flex: strengths.length,
                    backgroundColor: "#2E7831",
                    borderRadius: 1,
                  }}
                />
                <Box
                  sx={{
                    flex: weaknesses.length,
                    backgroundColor: "#EFC52E",
                    borderRadius: 1,
                  }}
                />
              </Box>
            )}
          </Card>

          {/* Strengths */}
          {strengths.length > 0 && (
            <Card>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  mb: 2,
                }}
              >
                <CheckCircleIcon sx={{ fontSize: 20, color: "#2E7831" }} />
                <Typography
                  variant="subtitle2"
                  sx={{ fontWeight: 600, color: "#18181b" }}
                >
                  Strengths
                </Typography>
              </Box>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                {strengths.map((s, i) => (
                  <Box
                    key={i}
                    sx={{
                      display: "flex",
                      alignItems: "flex-start",
                      gap: 1.5,
                    }}
                  >
                    <Box
                      sx={{
                        width: 6,
                        height: 6,
                        borderRadius: "50%",
                        backgroundColor: "#2E7831",
                        flexShrink: 0,
                        mt: 0.8,
                      }}
                    />
                    <Typography
                      variant="body2"
                      sx={{
                        color: "#3f3f46",
                        lineHeight: 1.6,
                        fontSize: "0.825rem",
                      }}
                    >
                      {s}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Card>
          )}

          {/* Weaknesses */}
          {weaknesses.length > 0 && (
            <Card>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  mb: 2,
                }}
              >
                <ErrorOutlineIcon sx={{ fontSize: 20, color: "#A48137" }} />
                <Typography
                  variant="subtitle2"
                  sx={{ fontWeight: 600, color: "#18181b" }}
                >
                  Areas for Improvement
                </Typography>
              </Box>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                {weaknesses.map((w, i) => (
                  <Box
                    key={i}
                    sx={{
                      display: "flex",
                      alignItems: "flex-start",
                      gap: 1.5,
                    }}
                  >
                    <Box
                      sx={{
                        width: 6,
                        height: 6,
                        borderRadius: "50%",
                        backgroundColor: "#A48137",
                        flexShrink: 0,
                        mt: 0.8,
                      }}
                    />
                    <Typography
                      variant="body2"
                      sx={{
                        color: "#3f3f46",
                        lineHeight: 1.6,
                        fontSize: "0.825rem",
                      }}
                    >
                      {w}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Card>
          )}

          {/* Recommendations */}
          {!recommendationsLoading && recommendations.length > 0 && (
            <Card>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  mb: 2,
                }}
              >
                <LightbulbOutlinedIcon
                  sx={{ fontSize: 20, color: "#7D5DA3" }}
                />
                <Typography
                  variant="subtitle2"
                  sx={{ fontWeight: 600, color: "#18181b" }}
                >
                  Recommended Actions
                </Typography>
              </Box>
              <Typography
                variant="caption"
                sx={{ color: "#71717a", display: "block", mb: 2 }}
              >
                Steps you can take to improve water and soil quality on your
                farm
              </Typography>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                {recommendations.map((r, i) => (
                  <Box
                    key={i}
                    sx={{
                      display: "flex",
                      alignItems: "flex-start",
                      gap: 1.5,
                      p: 1.5,
                      backgroundColor: "#F1EFF7",
                      borderRadius: 2,
                      border: "1px solid #E0DAEE",
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{
                        color: "#7D5DA3",
                        fontWeight: 700,
                        backgroundColor: "#E0DAEE",
                        width: 22,
                        height: 22,
                        borderRadius: "50%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                        mt: 0.1,
                      }}
                    >
                      {i + 1}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: "#3f3f46",
                        lineHeight: 1.6,
                        fontSize: "0.825rem",
                      }}
                    >
                      {r}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Card>
          )}
        </>
      )}
    </Box>
  );
}

// ─── Styled components ───
const Card = styled(Box)({
  backgroundColor: "white",
  borderRadius: 16,
  boxShadow: "0 2px 8px rgba(0, 0, 0, 0.08)",
  padding: 20,
  marginBottom: 16,
});
